import React, { useState, useEffect, useRef } from "react";
import * as echarts from 'echarts';
import DashboardMainContainer from "../common/Containers/DashboardMainContainer";
import {
  AdminPanelTitle,
  AdminMetric,
  AdminMainText,
  AdminMetadataText,
  AdminRowAction,
} from '../../sections/AdminDashboard/shared/adminUx';
import { AdminWidgetHeader } from './AdminWidgetChrome';

const PostRefreshReminderWidget = ({
  className = 'masonry-item w-[370px] h-[250px] flex flex-col',
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const chartRef = useRef(null);
  const progressRef = useRef(null);

  // Sample data
  const postData = {
    totalPosts: 10,
    lastRefresh: 31,
    refreshProgress: 85,
    // Bar chart data: red (left), yellow (center), green (right)
    redPosts: 2,      // 0-10 days
    yellowPosts: 3,   // 11-20 days  
    greenPosts: 5     // 21-30 days
  };

  useEffect(() => {
    // Vertical bar chart showing three bars: Red (left), Yellow (center), Green (right)
    if (chartRef.current) {
      const chart = echarts.init(chartRef.current);

      const categories = ['0-10', '11-20', '21-30'];
      const data = [postData.redPosts, postData.yellowPosts, postData.greenPosts];
      const maxVal = Math.max(...data);

      const option = {
        grid: {
          left: '0',
          right: '0',
          top: '10%',
          bottom: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: categories,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { 
            show: true,
            color: '#232A2E',
            fontfamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            fontSize: 11,
            fontWeight: 400,
            margin: 8
          }
        },
        yAxis: {
          type: 'value',
          min: 0,
          max: maxVal,
          show: false
        },
        series: [
          {
            name: 'Posts',
            type: 'bar',
            data: [
              { 
                value: postData.redPosts, 
                itemStyle: { 
                  color: '#FB1912', // alertred from Tailwind config
                  borderRadius: [4, 4, 4, 4]
                } 
              },
              { 
                value: postData.yellowPosts, 
                itemStyle: { 
                  color: '#F2C02D', // warmyellow from Tailwind config
                  borderRadius: [4, 4, 4, 4]
                } 
              },
              { 
                value: postData.greenPosts, 
                itemStyle: { 
                  color: '#03A62C', // freshgreen from Tailwind config
                  borderRadius: [4, 4, 4, 4]
                } 
              }
            ],
            barWidth: '80%',
            label: {
              show: true,
              position: 'inside',
              formatter: (params) => `${params.value}`,
              color: '#ffffff',
              fontfamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              fontSize: 11,
              fontWeight: 400,
              // verticalAlign: 'top',
              align: 'center',
              // offset: [0, '100%']
            }
          }
        ],
        tooltip: {
          show: false
        },
        animationDuration: 600
      };
      chart.setOption(option);
      return () => chart.dispose();
    }
  }, []);

  useEffect(() => {
    // Refresh Progress Bar
    if (progressRef.current) {
      const chart = echarts.init(progressRef.current);
      const option = {
        grid: { top: 0, right: 0, bottom: 0, left: 0 },
        xAxis: { show: false, type: 'value', max: 100 },
        yAxis: { show: false, type: 'category' },
        series: [{
          data: [postData.refreshProgress],
          type: 'bar',
          barWidth: '100%',
          itemStyle: { 
            borderRadius: 10,
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0.0, color: '#7B62D9' }, // softpurple from Tailwind config
              { offset: 0.33, color: '#7B62D9' },
              { offset: 0.33, color: '#F2C02D' }, // warmyellow from Tailwind config
              { offset: 0.66, color: '#F2C02D' },
              { offset: 0.66, color: '#FB1912' }, // alertred from Tailwind config
              { offset: 1.0, color: '#FB1912' }
            ])
          },
          label: { show: false }
        }]
      };
      chart.setOption(option);
      return () => chart.dispose();
    }
  }, []);

  const handleRefresh = () => {
    setIsRefreshing(true);
    // Simulate refresh process
    setTimeout(() => {
      setIsRefreshing(false);
    }, 2000);
  };

  return (
    <DashboardMainContainer className={className}>
      <AdminWidgetHeader title="Post Refresh Reminder" />
      
      <div className="flex-1 p-4 space-y-5">
        {/* Top Section - Overview Metrics */}
        <div className="flex items-start justify-between">
          {/* Left Side - Total Posts */}
          <div className="text-left">
            <AdminMetric size="large" className="text-offblack tabular-nums">
              {postData.totalPosts}
            </AdminMetric>
            <AdminPanelTitle className="text-offblack">Active Posts</AdminPanelTitle>
          </div>
          
          {/* Right Side - Bar Chart */}
          <div className="text-center">
            <div className="relative">
              <div ref={chartRef} className="w-44 h-32 -mt-1 -mb-5"></div>
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className="border-t border-lightgray"></div>

                 {/* Bottom Section - Individual Post Reminder */}
         <div className="flex items-center justify-between w-full">
           {/* Left Side - Post Details and Progress Bar */}
           <div className="space-y-1 w-full">
            <div className="flex items-center justify-between -mt-2">
            <div className="flex flex-col">
             <AdminMainText className="text-offblack leading-none block">Post Title</AdminMainText>
             <AdminMetadataText className="text-offblack">
               Last refreshed {postData.lastRefresh} days ago
             </AdminMetadataText>
             </div>

           <AdminRowAction
             onClick={handleRefresh}
             disabled={isRefreshing}
           >
             {isRefreshing ? 'Refreshing...' : 'Refresh'}
           </AdminRowAction>

             </div>
             {/* Progress Bar below the "Last refreshed" text */}
             {/* <div ref={progressRef} className="w-full h-2 bg-lightgray"></div> */}
           </div>
           
         </div>
      </div>
    </DashboardMainContainer>
  );
};

export default PostRefreshReminderWidget;
